import { Device, isMobile, PageType } from '@/utils/app/detect'
import { Context } from '@/types/app'
import { AdZone, DeviceType, MoltenBundle } from './AdAlliance.types'

type MoltenBootstrap = { cmd: Array<() => void> }
type MoltenGlobal = MoltenBundle | MoltenBootstrap

declare global {
  interface Window {
    MoltenBundle?: MoltenGlobal
    __ADA_CTX_HASH__?: string
    __ADA_CMD__?: Array<() => void>
    __ADA_ANY_SLOT_PUSHED__?: boolean
    __ADA_ZONE_READY__?: boolean
    __ADA_PENDING_SLOTS__?: Array<string>
  }
}

function isMoltenBundle(x: unknown): x is MoltenBundle {
  return (
    !!x &&
    typeof x === 'object' &&
    'getClient' in (x as any) &&
    'push' in (x as any)
  )
}

function ensureBootstrap(): MoltenBootstrap {
  if (typeof window === 'undefined') return { cmd: [] }
  const w = window as Window &
    Required<Pick<Window, 'MoltenBundle' | '__ADA_CMD__'>>

  if (!Array.isArray(w.__ADA_CMD__)) {
    ;(window as any).__ADA_CMD__ = []
  }

  if (!w.MoltenBundle || typeof w.MoltenBundle !== 'object') {
    w.MoltenBundle = { cmd: (window as any).__ADA_CMD__ }
  } else {
    const mb = w.MoltenBundle as any
    if (!Array.isArray(mb.cmd)) {
      mb.cmd = (window as any).__ADA_CMD__
    } else if (mb.cmd !== (window as any).__ADA_CMD__) {
      ;(window as any).__ADA_CMD__.push(...mb.cmd)
      mb.cmd = (window as any).__ADA_CMD__
    }
  }

  return w.MoltenBundle as MoltenBootstrap
}

function queue(fn: (mb: MoltenBundle) => void) {
  const boot = ensureBootstrap()
  console.log('[AdA:queue] add callback. current len=', boot.cmd.length)
  boot.cmd.push(() => {
    const current = window.MoltenBundle
    console.log(
      '[AdA:queue] draining callback. hasSDK=',
      isMoltenBundle(current),
    )
    if (isMoltenBundle(current)) fn(current)
  })
}

export const initialize = (context: Context) => {
  ensureBootstrap()
  console.log('[AdA:init] ctx', context)

  const adZone = pageTypeToAdZone(context.pageType)
  const deviceType = deviceToSiteType(context.device)
  const keywords = context.keywords ?? []

  queue((mb) => {
    console.log('[AdA:init->client] set config', {
      adZone,
      deviceType,
      keywords,
    })
    const client = mb.getClient()
    client.setSiteType(deviceType)
    client.setZone(adZone)
    client.setKeywords(keywords)
    client.setIsIndexPage(adZone === AdZone.HOMEPAGE)

    window.__ADA_ZONE_READY__ = true

    if (
      Array.isArray(window.__ADA_PENDING_SLOTS__) &&
      window.__ADA_PENDING_SLOTS__.length
    ) {
      const toFlush = [...window.__ADA_PENDING_SLOTS__]
      window.__ADA_PENDING_SLOTS__ = []
      toFlush.forEach((slotId) => {
        try {
          mb.push(slotId.toLowerCase())
        } catch (e) {
          console.warn('[AdA:init] flush pending slot failed', slotId, e)
        }
      })
    }
  })
}

export const pushAdSlot = (id: string) => {
  if (!id) return
  ensureBootstrap()
  console.log('[AdA:pushAdSlot] queue', id)
  queue((mb) => {
    const doPush = () => {
      console.log('[AdA:pushAdSlot] run → mb.push', id.toLowerCase())
      mb.push(id.toLowerCase())
      window.__ADA_ANY_SLOT_PUSHED__ = true
    }

    if (window.__ADA_ZONE_READY__) {
      doPush()
    } else {
      if (!Array.isArray(window.__ADA_PENDING_SLOTS__)) {
        window.__ADA_PENDING_SLOTS__ = []
      }
      window.__ADA_PENDING_SLOTS__!.push(id)
      console.log('[AdA:pushAdSlot] deferred until zone ready', id)
    }
  })
}

const deviceToSiteType = (device: Device) =>
  isMobile(device) ? DeviceType.Mobile : DeviceType.Desktop

export const pageTypeToAdZone = (pageType: PageType): AdZone => {
  switch (pageType) {
    case PageType.ARTICLE:
      return AdZone.ARTIKEL
    case PageType.HOME:
      return AdZone.HOMEPAGE
    case PageType.MAPS:
      return AdZone.KARTE
    case PageType.CLIMATE_UPDATE:
      return AdZone.KLIMA_UPDATE
    case PageType.ODS:
      return AdZone.ORTE
    default:
      return AdZone.DEFAULT
  }
}

export const AdAllianceLib = { initialize, pushAdSlot }
